import { concepts, graphemes, irregularWords, lessons, mappings, phonemes, sentences, stages, words } from '../data/foundations'

export interface ValidationIssue {
  level: 'error' | 'warning'
  code: string
  message: string
}

const duplicates = (ids: string[]) => ids.filter((id, index) => ids.indexOf(id) !== index)

export const validateCurriculum = (): ValidationIssue[] => {
  const issues: ValidationIssue[] = []
  const add = (level: ValidationIssue['level'], code: string, message: string) => issues.push({ level, code, message })
  const collections: Array<[string, string[]]> = [
    ['phoneme', phonemes.map((item) => item.id)], ['grapheme', graphemes.map((item) => item.id)],
    ['mapping', mappings.map((item) => item.id)], ['concept', concepts.map((item) => item.id)],
    ['word', words.map((item) => item.id)], ['lesson', lessons.map((item) => item.id)],
    ['sentence', sentences.map((item) => item.id)], ['irregular word', irregularWords.map((item) => item.id)],
  ]
  collections.forEach(([kind, ids]) => duplicates(ids).forEach((id) => add('error', 'DUPLICATE_ID', `Duplicate ${kind} id: ${id}`)))

  const phonemeIds = new Set(phonemes.map((item) => item.id))
  const graphemeIds = new Set(graphemes.map((item) => item.id))
  const mappingIds = new Set(mappings.map((item) => item.id))
  const conceptIds = new Set(concepts.map((item) => item.id))
  const wordIds = new Set(words.map((item) => item.id))
  const lessonIds = new Set(lessons.map((item) => item.id))
  const irregularIds = new Set(irregularWords.map((item) => item.id))

  mappings.forEach((mapping) => {
    if (!graphemeIds.has(mapping.graphemeId)) add('error', 'MISSING_GRAPHEME', `${mapping.id} references ${mapping.graphemeId}`)
    if (!conceptIds.has(mapping.conceptId)) add('error', 'MISSING_CONCEPT', `${mapping.id} references ${mapping.conceptId}`)
    mapping.phonemeIds.forEach((id) => { if (!phonemeIds.has(id)) add('error', 'MISSING_PHONEME', `${mapping.id} references ${id}`) })
  })

  concepts.forEach((concept) => {
    concept.prerequisiteIds.forEach((id) => { if (!conceptIds.has(id)) add('error', 'MISSING_PREREQUISITE', `${concept.id} references ${id}`) })
    concept.mappingIds.forEach((id) => { if (!mappingIds.has(id)) add('error', 'MISSING_MAPPING', `${concept.id} references ${id}`) })
    concept.phonemeIds.forEach((id) => { if (!phonemeIds.has(id)) add('error', 'MISSING_PHONEME', `${concept.id} references ${id}`) })
    concept.graphemeIds.forEach((id) => { if (!graphemeIds.has(id)) add('error', 'MISSING_GRAPHEME', `${concept.id} references ${id}`) })
    concept.exampleWordIds.forEach((id) => { if (!wordIds.has(id)) add('warning', 'EXAMPLE_NOT_IN_BANK', `${concept.id} example ${id} is not in the word bank`) })
  })

  const visiting = new Set<string>()
  const visited = new Set<string>()
  const visit = (id: string, trail: string[]) => {
    if (visiting.has(id)) {
      add('error', 'PREREQUISITE_CYCLE', [...trail, id].join(' -> '))
      return
    }
    if (visited.has(id)) return
    visiting.add(id)
    concepts.find((item) => item.id === id)?.prerequisiteIds.forEach((next) => visit(next, [...trail, id]))
    visiting.delete(id)
    visited.add(id)
  }
  concepts.forEach((concept) => visit(concept.id, []))

  concepts.forEach((concept) => concept.prerequisiteIds.forEach((id) => {
    const prerequisite = concepts.find((item) => item.id === id)
    if (prerequisite && prerequisite.order >= concept.order) add('error', 'STAGE_ORDER', `${concept.id} depends on later ${id}`)
  }))

  words.forEach((word) => {
    if (word.mapping.length !== word.graphemes.length || word.mapping.length !== word.graphemeIds.length) add('error', 'MAPPING_COUNT', `${word.id} mapping/grapheme counts differ`)
    if (word.phonemeIds.join('|') !== word.mapping.flatMap((item) => item.phonemeIds).join('|')) add('error', 'PHONEME_SEGMENTATION', `${word.id} phoneme sequence differs from mapping sequence`)
    const coverage = Array.from({ length: word.spelling.length }, () => 0)
    word.mapping.forEach((entry) => {
      if (!mappingIds.has(entry.mappingId)) add('error', 'MISSING_MAPPING', `${word.id} references ${entry.mappingId}`)
      entry.spellingParts.forEach((part) => {
        for (let index = part.start; index < part.start + part.length; index += 1) coverage[index] = (coverage[index] ?? 0) + 1
      })
    })
    if (coverage.some((count) => count !== 1)) add('error', 'SPELLING_SEGMENTATION', `${word.id} does not cover each spelling character exactly once: ${coverage.join(',')}`)
    if (!conceptIds.has(word.introducedAtConceptId)) add('error', 'MISSING_CONCEPT', `${word.id} introduced at unknown ${word.introducedAtConceptId}`)
    word.decodableAfterConcepts.forEach((id) => { if (!conceptIds.has(id)) add('error', 'MISSING_CONCEPT', `${word.id} requires ${id}`) })
  })

  lessons.forEach((lesson) => {
    if (!conceptIds.has(lesson.conceptId)) add('error', 'MISSING_CONCEPT', `${lesson.id} references ${lesson.conceptId}`)
    lesson.targetMappingIds.forEach((id) => { if (!mappingIds.has(id)) add('error', 'MISSING_MAPPING', `${lesson.id} targets ${id}`) })
    lesson.wordIds.forEach((id) => { if (!wordIds.has(id)) add('error', 'MISSING_WORD', `${lesson.id} references ${id}`) })
  })

  sentences.forEach((sentence) => {
    sentence.wordIds.forEach((id) => { if (!wordIds.has(id)) add('error', 'MISSING_WORD', `${sentence.id} references ${id}`) })
    sentence.requiredConceptIds.forEach((id) => { if (!conceptIds.has(id)) add('error', 'MISSING_CONCEPT', `${sentence.id} requires ${id}`) })
    sentence.irregularWordIds.forEach((id) => { if (!irregularIds.has(id)) add('error', 'MISSING_IRREGULAR_WORD', `${sentence.id} references ${id}`) })
  })

  stages.forEach((stage) => stage.conceptIds.forEach((id) => {
    const concept = concepts.find((item) => item.id === id)
    if (!concept) add('error', 'MISSING_CONCEPT', `${stage.id} references ${id}`)
    else if (concept.stage !== stage.number) add('error', 'STAGE_ORDER', `${id} belongs to Stage ${concept.stage}, not ${stage.number}`)
  }))

  const usedMappingIds = new Set(words.flatMap((word) => word.mapping.map((entry) => entry.mappingId)))
  mappings.forEach((mapping) => { if (!usedMappingIds.has(mapping.id)) add('warning', 'ORPHAN_MAPPING', `${mapping.id} has no word record yet`) })
  const usedLessonIds = new Set(lessons.map((lesson) => lesson.id))
  lessonIds.forEach((id) => { if (!usedLessonIds.has(id)) add('error', 'ORPHAN_LESSON', id) })
  return issues
}

